<template>
    <div class="overflow-auto md:-mb-3 md:-mx-6 md:-mt-6 flex-1">
        <transition name="wizard-view" mode="out-in">
            <div
                v-if="wizardView === 'intro'"
                key="intro"
                class="h-full flex max-w-(--breakpoint-sm) mx-auto flex-col relative"
            >
                <div class="flex-1">
                    <div class="max-w-2xl mx-auto px-6 py-8">
                        <intelligent-inbox-wizard-intro @start="startWizard()"></intelligent-inbox-wizard-intro>
                    </div>
                </div>
            </div>

            <div
                v-else-if="wizardView === 'wizard'"
                key="wizard"
                class="h-full flex max-w-(--breakpoint-sm) mx-auto flex-col relative gap-10"
            >
                <div class="absolute flex flex-col items-center inset-0 z-10 pointer-events-none">
                    <wizard-progress-bar
                        :currentStep="currentStep"
                        :stepLabels="stepLabels"
                        :stepIcons="stepIcons"
                    ></wizard-progress-bar>
                </div>

                <div ref="stepContainerRef" class="flex-1 pt-[240px]">
                    <animate-presence mode="wait">
                        <motion.div
                            :key="currentStep"
                            :initial="{ x: 60 * direction, opacity: 0 }"
                            :animate="{ x: 0, opacity: 1 }"
                            :exit="{ x: -60 * direction, opacity: 0 }"
                            :transition="quickTransition"
                        >
                            <component :is="steps[currentStep]" v-model="wizardData"></component>
                        </motion.div>
                    </animate-presence>
                </div>

                <vx-sticky-footer>
                    <vx-button @click="goBack()" class="w-20" size="lg" color="muted"> Back </vx-button>
                    <vx-button
                        @click="goForward()"
                        data-test="continue"
                        type="button"
                        class="grow"
                        size="lg"
                        color="primary"
                    >
                        {{ isLastContentStep ? 'Complete Setup' : 'Continue' }}
                        <font-awesome-icon :icon="faArrowRight"></font-awesome-icon>
                    </vx-button>
                </vx-sticky-footer>
            </div>

            <div v-else key="success" class="h-full flex max-w-(--breakpoint-sm) mx-auto flex-col relative">
                <div class="flex-1">
                    <div class="max-w-2xl mx-auto px-6 py-8">
                        <intelligent-inbox-wizard-success></intelligent-inbox-wizard-success>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script lang="ts" setup>
import { faArrowRight, faFolderTree, faListCheck, faMagnifyingGlass, faStore } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxStickyFooter } from '@voxie/frontend-components';
import { AnimatePresence, motion } from 'motion-v';
import { computed, nextTick, ref, type Component } from 'vue';
import { useIntelligentInbox } from '../useIntelligentInbox';
import { quickTransition } from './wizardMotionVariants';
import WizardProgressBar from './WizardProgressBar.vue';
import IntelligentInboxWizardBrandContext from './IntelligentInboxWizardBrandContext.vue';
import IntelligentInboxWizardOpportunityDetection from './IntelligentInboxWizardOpportunityDetection.vue';
import IntelligentInboxWizardStatusDefinition from './IntelligentInboxWizardStatusDefinition.vue';
import IntelligentInboxWizardCategories from './IntelligentInboxWizardCategories.vue';
import IntelligentInboxWizardSuccess from './IntelligentInboxWizardSuccess.vue';
import IntelligentInboxWizardIntro from './IntelligentInboxWizardIntro.vue';

const { wizardData, wizardView, currentStep } = useIntelligentInbox();
const direction = ref(1);
const stepContainerRef = ref<HTMLElement | null>(null);

const focusFirstInput = (): void => {
    setTimeout(() => {
        const input = stepContainerRef.value?.querySelector<HTMLElement>('input, textarea');
        input?.focus();
    }, 600);
};

const steps: Component[] = [
    IntelligentInboxWizardBrandContext,
    IntelligentInboxWizardOpportunityDetection,
    IntelligentInboxWizardStatusDefinition,
    IntelligentInboxWizardCategories,
];

const stepLabels = ['Brand', 'Detection', 'Statuses', 'Categories'];

const stepIcons = [faStore, faMagnifyingGlass, faListCheck, faFolderTree];

const isLastContentStep = computed(() => currentStep.value === steps.length - 1);

const startWizard = (): void => {
    wizardView.value = 'wizard';
    focusFirstInput();
};

const goForward = (): void => {
    if (isLastContentStep.value) {
        wizardView.value = 'success';
    } else {
        direction.value = 1;
        nextTick(() => {
            currentStep.value += 1;
        });
        focusFirstInput();
    }
};

const goBack = (): void => {
    if (currentStep.value === 0) {
        wizardView.value = 'intro';
    } else {
        direction.value = -1;
        nextTick(() => {
            currentStep.value -= 1;
        });
    }
};
</script>

<style scoped>
.wizard-view-enter-active,
.wizard-view-leave-active {
    transition: opacity 0.3s ease;
}

.wizard-view-enter-from,
.wizard-view-leave-to {
    opacity: 0;
}
</style>
